import type { BehaviorDirection, BehaviorSetting, BehaviorsState } from '../../behaviors/index.js'

import type { FacilityConfig } from '../../facilities/index.js'

import type { WorkerTier } from '../../core/index.js'

/** Mirrors the API's `POST /admin/scoring/simulate` request. Rates are fractions (0.2 = 20%). */
export interface BehaviorChannelSetting {
  enabled: boolean
  weight: number
  best: number
  worst: number
}

export const STANDARD_KEYS = ['call_out_rate', 'on_time_arrival', 'charting_completion', 'shift_completion', 'no_show_rate'] as const
export type StandardKey = (typeof STANDARD_KEYS)[number]

export const STANDARD_LABELS: Record<StandardKey, string> = {
  call_out_rate: 'Call-Out Rate',
  on_time_arrival: 'On-Time Arrival',
  charting_completion: 'Charting Completion',
  shift_completion: 'Shift Completion',
  no_show_rate: 'No-Show Rate',
}

export type CustomBasis = 'per_period' | 'per_shift' | 'per_period_minimum'

export interface CustomChannel {
  key: string
  label: string
  basis: CustomBasis
  occurrences: number
  setting: BehaviorChannelSetting
}

export interface SimulatorSettings {
  periodDays: number
  standardPeriodDays: number
  activity: {
    scheduledShifts: number
    callOuts: number
    shortNoticeCallOuts: number
    noShows: number
    onTimeArrivals: number
    completedShifts: number
    /** Worked shifts that reported their chart (one shift, one chart), and how many were completed. */
    chartsDue: number
    chartsCompleted: number
    /** A summary percentage, used only while no shift reports a chart. */
    chartingPct: number | null
    clinicalBridgeShifts: number
    /** The scheduling events (phase E): denied PTO days worked, critical gaps filled and worked, late Bridge withdrawals. */
    ptoDeniedWorkedShifts: number
    lateFills: number
    lateBridgeWithdrawals: number
  }
  behaviors: Record<StandardKey, BehaviorChannelSetting>
  customBehaviors: CustomChannel[]
  rules: {
    minShifts: number
    shortNoticeMultiplier: number
    bridgeBoostPerShift: number
    ptoDeniedWorkedPoints: number
    lateFillPoints: number
    lateBridgeWithdrawalPoints: number
    previousScore: number | null
  }
  tierThresholds: { goldMinScore: number; silverMinScore: number; bronzeMinScore: number }
}

export interface ScoredInput {
  key: string
  label: string
  included: boolean
  excludedReason: 'switched_off' | 'no_weight' | 'no_data' | null
  measure: number | null
  numerator: number | null
  denominator: number | null
  best: number
  worst: number
  weight: number
  value: number | null
  effectiveWeight: number | null
  contribution: number | null
  pointsLost: number | null
}

export interface ScoreResult {
  score: number | null
  weightedScore: number | null
  tier: WorkerTier | null
  nextTier: { tier: WorkerTier; minScore: number; pointsNeeded: number } | null
  provisional: boolean
  workedShifts: number
  includedWeight: number
  inputs: ScoredInput[]
  adjustments: { kind: string; description: string; points: number }[]
  nextCleanShiftGain: number | null
}

/** The proposed defaults in docs/scoring/scoring-engine-specification.md, with that document's worked example as the activity. */
export const DEFAULT_SETTINGS: SimulatorSettings = {
  periodDays: 30,
  standardPeriodDays: 30,
  activity: { scheduledShifts: 18, callOuts: 1, shortNoticeCallOuts: 0, noShows: 1, onTimeArrivals: 14, completedShifts: 15, chartsDue: 0, chartsCompleted: 0, chartingPct: null, clinicalBridgeShifts: 0, ptoDeniedWorkedShifts: 0, lateFills: 0, lateBridgeWithdrawals: 0 },
  behaviors: {
    call_out_rate: { enabled: true, weight: 30, best: 0, worst: 0.2 },
    on_time_arrival: { enabled: true, weight: 20, best: 1, worst: 0.7 },
    charting_completion: { enabled: true, weight: 25, best: 1, worst: 0.6 },
    shift_completion: { enabled: true, weight: 20, best: 1, worst: 0.8 },
    no_show_rate: { enabled: true, weight: 5, best: 0, worst: 0.1 },
  },
  customBehaviors: [],
  rules: { minShifts: 5, shortNoticeMultiplier: 1, bridgeBoostPerShift: 5, ptoDeniedWorkedPoints: 2, lateFillPoints: 2, lateBridgeWithdrawalPoints: 1, previousScore: null },
  tierThresholds: { goldMinScore: 80, silverMinScore: 60, bronzeMinScore: 40 },
}

/** Same engine settings, with a month in which nothing went wrong. */
export function perfectMonth(settings: SimulatorSettings): SimulatorSettings {
  const scheduledShifts = Math.max(settings.activity.scheduledShifts, 1)
  return {
    ...settings,
    activity: { ...settings.activity, scheduledShifts, callOuts: 0, shortNoticeCallOuts: 0, noShows: 0, onTimeArrivals: scheduledShifts, completedShifts: scheduledShifts, chartsCompleted: Math.min(settings.activity.chartsDue, scheduledShifts) },
  }
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

/**
 * Makes a set of fader positions something that could actually happen, by
 * pulling dependent values into range: no more call-outs than shifts, no
 * more on-time arrivals than shifts worked, thresholds in order. Faders
 * move independently, so this runs after every change rather than each
 * fader having to know about the others.
 */
export function normalize(settings: SimulatorSettings): SimulatorSettings {
  const { activity, tierThresholds } = settings
  const scheduledShifts = Math.max(0, activity.scheduledShifts)
  const callOuts = clamp(activity.callOuts, 0, scheduledShifts)
  const noShows = clamp(activity.noShows, 0, scheduledShifts - callOuts)
  const worked = scheduledShifts - callOuts - noShows
  const goldMinScore = clamp(tierThresholds.goldMinScore, 0, 100)
  const silverMinScore = clamp(tierThresholds.silverMinScore, 0, goldMinScore)

  return {
    ...settings,
    activity: {
      ...activity,
      scheduledShifts,
      callOuts,
      noShows,
      shortNoticeCallOuts: clamp(activity.shortNoticeCallOuts, 0, callOuts),
      onTimeArrivals: clamp(activity.onTimeArrivals, 0, worked),
      completedShifts: clamp(activity.completedShifts, 0, worked),
      chartsDue: clamp(activity.chartsDue, 0, worked),
      chartsCompleted: clamp(activity.chartsCompleted, 0, clamp(activity.chartsDue, 0, worked)),
    },
    tierThresholds: { goldMinScore, silverMinScore, bronzeMinScore: clamp(tierThresholds.bronzeMinScore, 0, silverMinScore) },
  }
}

/** A minimum per period must be a whole number of at least one; any other behavior's two anchors must differ. */
export function channelIsInvalid(basis: CustomBasis, setting: Pick<BehaviorChannelSetting, 'best' | 'worst'>): boolean {
  return basis === 'per_period_minimum' ? !(Number.isInteger(setting.best) && setting.best >= 1) : setting.best === setting.worst
}

/** A behavior that cannot be scored - anchors that coincide, or a minimum that is not a whole number of at least one; the API rejects it, so it is named here first. */
export function invalidChannels(settings: SimulatorSettings): string[] {
  return [
    ...STANDARD_KEYS.filter((key) => settings.behaviors[key].best === settings.behaviors[key].worst).map((key) => STANDARD_LABELS[key]),
    ...settings.customBehaviors.filter((behavior) => channelIsInvalid(behavior.basis, behavior.setting)).map((behavior) => behavior.label),
  ]
}

/** Sensible starting anchors for a new custom channel, by what it counts and which way it counts. */
export function defaultCustomSetting(basis: CustomBasis, direction: BehaviorDirection): BehaviorChannelSetting {
  if (basis === 'per_period_minimum') {
    return { enabled: true, weight: 10, best: 1, worst: 0 }
  }
  if (basis === 'per_period') {
    return direction === 'positive' ? { enabled: true, weight: 10, best: 1, worst: 0 } : { enabled: true, weight: 10, best: 0, worst: 3 }
  }
  return direction === 'positive' ? { enabled: true, weight: 10, best: 1, worst: 0 } : { enabled: true, weight: 10, best: 0, worst: 0.2 }
}

/** Which of a client's settings to bring onto the desk: the master, or what one facility actually applies. */
export type BehaviorsSource = { kind: 'master' } | { kind: 'facility'; facilityId: string }

const toChannelSetting = ({ enabled, weight, best, worst }: BehaviorSetting): BehaviorChannelSetting => ({ enabled, weight, best, worst })

/**
 * Takes a real client's behaviors into the simulator - which are on, their
 * weights and their anchors - either the master or one facility's effective
 * settings. Custom behaviors arrive as channels with the client's basis and
 * anchors, keeping any occurrence count already dialled in for them.
 */
export function withClientBehaviors(settings: SimulatorSettings, state: BehaviorsState, source: BehaviorsSource): SimulatorSettings {
  const facility = source.kind === 'facility' ? state.facilities.find((candidate) => candidate.facilityId === source.facilityId) : undefined
  const settingFor = (key: string): BehaviorSetting | undefined =>
    facility ? facility.effective.find((behavior) => behavior.key === key) : state.master[key]

  const behaviors = Object.fromEntries(
    STANDARD_KEYS.map((key) => {
      const setting = settingFor(key)
      return [key, setting ? toChannelSetting(setting) : settings.behaviors[key]]
    }),
  ) as Record<StandardKey, BehaviorChannelSetting>

  const customBehaviors = state.behaviors
    .filter((behavior) => behavior.kind === 'custom')
    .map((behavior): CustomChannel => {
      const setting = settingFor(behavior.key)
      const existing = settings.customBehaviors.find((channel) => channel.key === behavior.key)
      const basis = behavior.basis ?? 'per_period'
      return {
        key: behavior.key,
        label: behavior.label,
        basis,
        occurrences: existing?.occurrences ?? 0,
        setting: setting ? toChannelSetting(setting) : (existing?.setting ?? defaultCustomSetting(basis, behavior.direction)),
      }
    })

  return { ...settings, behaviors, customBehaviors }
}

/**
 * A real score handed to the simulator from elsewhere in the admin area:
 * the engine input it was computed from, and what to call it. Kept in
 * sessionStorage for the one navigation, then cleared.
 */
export interface SimulatorHandoff {
  label: string
  settings: SimulatorSettings
  /** Whose score this is, when it is one worker's. The simulator names them above the desk for as long as their numbers are on it. */
  subject?: SimulatedWorker
}

/** The worker a handed-over score belongs to. The facility is null when the worker's profile could not be read. */
export interface SimulatedWorker {
  clientId: string
  workerId: string
  workerName: string
  facilityId: string | null
  facilityName: string | null
  /** The period the score covers, as shown to the reader. */
  period: string
}

const isStringOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string'

function toSimulatedWorker(value: unknown): SimulatedWorker | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined
  }
  const { clientId, workerId, workerName, facilityId, facilityName, period } = value as Record<string, unknown>
  return typeof clientId === 'string' && typeof workerId === 'string' && typeof workerName === 'string' && typeof period === 'string' && isStringOrNull(facilityId) && isStringOrNull(facilityName)
    ? { clientId, workerId, workerName, facilityId, facilityName, period }
    : undefined
}

const HANDOFF_KEY = 'autonome-web:simulator-handoff'

export function writeHandoff(handoff: SimulatorHandoff): void {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(handoff))
  } catch {
    // Storage unavailable (private window, quota): the simulator simply opens at its defaults.
  }
}

/** Reads and clears the handoff, so a later visit to the simulator starts fresh. */
export function takeHandoff(): SimulatorHandoff | null {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY)
    if (raw === null) {
      return null
    }
    sessionStorage.removeItem(HANDOFF_KEY)
    const parsed = JSON.parse(raw) as Partial<SimulatorHandoff>
    if (typeof parsed.label !== 'string' || !parsed.settings || typeof parsed.settings !== 'object') {
      return null
    }
    const subject = toSimulatedWorker(parsed.subject)
    // A stored score names a scheduling event's count and points only when it had one: the rest read as none, at the defaults.
    const settings = parsed.settings as SimulatorSettings
    const filled: SimulatorSettings = {
      ...settings,
      activity: { ...DEFAULT_SETTINGS.activity, ...settings.activity, ptoDeniedWorkedShifts: settings.activity?.ptoDeniedWorkedShifts ?? 0, lateFills: settings.activity?.lateFills ?? 0, lateBridgeWithdrawals: settings.activity?.lateBridgeWithdrawals ?? 0 },
      rules: { ...DEFAULT_SETTINGS.rules, ...settings.rules },
    }
    return { label: parsed.label, settings: filled, ...(subject ? { subject } : {}) }
  } catch {
    return null
  }
}

/** Takes a facility's scoring rules and tier thresholds into the simulator, leaving the activity alone. */
export function withFacilityConfig(settings: SimulatorSettings, config: FacilityConfig): SimulatorSettings {
  const scoring = config.scoring
  return {
    ...settings,
    rules: {
      ...settings.rules,
      minShifts: scoring?.minShifts ?? settings.rules.minShifts,
      shortNoticeMultiplier: scoring?.shortNoticeMultiplier ?? settings.rules.shortNoticeMultiplier,
      bridgeBoostPerShift: config.clinicalBridge.scoreBoostPerShift,
      ptoDeniedWorkedPoints: scoring?.ptoDeniedWorkedPoints ?? settings.rules.ptoDeniedWorkedPoints,
      lateFillPoints: scoring?.lateFillPoints ?? settings.rules.lateFillPoints,
      lateBridgeWithdrawalPoints: scoring?.lateBridgeWithdrawalPoints ?? settings.rules.lateBridgeWithdrawalPoints,
    },
    tierThresholds: {
      goldMinScore: config.tierThresholds.goldMinScore,
      silverMinScore: config.tierThresholds.silverMinScore,
      bronzeMinScore: config.tierThresholds.bronzeMinScore,
    },
  }
}
